import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { UserPlus, ArrowRight, Trash2 } from "lucide-react";
import { toast } from "sonner";

export default function Workers() {
  const [workers, setWorkers] = useState([]);
  const [stats, setStats] = useState([]);
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const [w, s] = await Promise.all([
        api.get("/workers"),
        api.get("/stats/by-worker"),
      ]);
      setWorkers(w.data);
      setStats(s.data);
    } catch {
      toast.error("Yüklenemedi");
    }
  };
  useEffect(() => { load(); }, []);

  const add = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    setSaving(true);
    try {
      await api.post("/workers", { name: name.trim() });
      toast.success("Çalışan eklendi");
      setName("");
      setOpen(false);
      load();
    } catch {
      toast.error("Eklenemedi");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Bu çalışan ve tüm işleri silinecek. Emin misiniz?")) return;
    try {
      await api.delete(`/workers/${id}`);
      toast.success("Çalışan silindi");
      load();
    } catch {
      toast.error("Silinemedi");
    }
  };

  const statMap = Object.fromEntries(stats.map(s => [s.worker_id, s]));

  return (
    <div className="space-y-8">
      <section className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 fade-up">
        <div>
          <div className="text-xs font-semibold tracking-[0.22em] uppercase text-[#D98359]">Ekibiniz</div>
          <h1 className="mt-2 font-display text-3xl sm:text-4xl font-semibold text-[#1A1D1A]">Çalışanlar</h1>
          <div className="brand-underline mt-3 w-16" />
          <p className="mt-4 text-[#5C605C] max-w-xl">İş verdiğiniz kişiler ve genel iş yükleri.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="rounded-full h-12 px-6 bg-[#3B5249] hover:bg-[#2C3E37] text-white shadow-soft transition-colors" data-testid="add-worker-btn">
              <UserPlus size={18} className="mr-1.5" />
              Yeni Çalışan
            </Button>
          </DialogTrigger>
          <DialogContent className="rounded-2xl">
            <DialogHeader>
              <DialogTitle className="font-display">Yeni Çalışan Ekle</DialogTitle>
            </DialogHeader>
            <form onSubmit={add} className="space-y-4">
              <div>
                <label className="text-xs font-semibold tracking-widest uppercase text-[#5C605C]">İsim</label>
                <Input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Örn: Ayşe"
                  className="mt-1.5 h-12 rounded-xl border-[#E6E6E2] focus-visible:ring-[#3B5249]/25"
                  data-testid="worker-name-input"
                  autoFocus
                />
              </div>
              <DialogFooter>
                <Button type="submit" disabled={saving} className="rounded-full h-11 px-6 bg-[#3B5249] hover:bg-[#2C3E37] text-white transition-colors" data-testid="save-worker-btn">
                  {saving ? "Kaydediliyor..." : "Kaydet"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </section>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {workers.map((w, i) => {
          const s = statMap[w.id] || { total_works: 0, pending_quantity: 0, total_given: 0 };
          return (
            <Card
              key={w.id}
              className="rounded-2xl border border-[#E6E6E2] bg-white shadow-soft p-6 card-hover fade-up"
              style={{ animationDelay: `${i * 40}ms` }}
              data-testid={`worker-${w.name.toLowerCase()}`}
            >
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3.5">
                  <div className="h-14 w-14 rounded-2xl bg-gradient-to-br from-[#3B5249] to-[#5C7D64] text-white grid place-items-center font-display font-semibold text-xl">
                    {w.name.charAt(0)}
                  </div>
                  <div>
                    <div className="font-display font-semibold text-lg text-[#1A1D1A]">{w.name}</div>
                    <div className="text-xs text-[#5C605C]">{s.total_works} iş kaydı</div>
                  </div>
                </div>
                <Button variant="ghost" size="icon" onClick={() => remove(w.id)} className="text-[#5C605C] hover:text-[#A85F26] hover:bg-[#FBEEDF] rounded-full transition-colors" data-testid={`delete-worker-${w.id}`}>
                  <Trash2 size={16} />
                </Button>
              </div>

              <div className="mt-6 flex items-end justify-between">
                <div>
                  <div className="text-[10px] font-semibold tracking-widest uppercase text-[#A85F26]">Bekleyen Adet</div>
                  <div className="font-display text-3xl font-semibold text-[#A85F26]">{s.pending_quantity}</div>
                </div>
                <Link
                  to={`/workers/${w.id}`}
                  className="inline-flex items-center gap-1 text-sm font-medium text-[#3B5249] hover:text-[#2C3E37] transition-colors"
                  data-testid={`worker-detail-link-${w.id}`}
                >
                  Detay <ArrowRight size={14} />
                </Link>
              </div>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
